import { supabase } from '@/lib/supabase';
import type { AbsenceFactRow } from '@/features/dashboard/data';

/**
 * Manage Records' own read — deliberately NOT the Dashboard's `recentFacts`.
 *
 * 🔴 Adversarial-review finding: `recentFacts` is capped at
 * `ACTIVITY_LOG_LIMIT` (200 absence-day rows, company-wide), sized for a
 * rolling READ-ONLY activity feed. Reusing it as this WRITE surface's data
 * source meant that once total history passed 200 rows, an older decided
 * record would silently disappear from Manage Records — unvoidable,
 * uneditable, with no indication anything was missing. Fixed by giving this
 * page its own unbounded fetch, same precedent as the CSV export
 * (`downloadAbsenceHistoryCsv` in dashboard/data.ts): the dataset is ~70 rows
 * today and grows by a handful a week, so this is years away — page it
 * before it ever gets close, per that export's own comment.
 *
 * Filtered server-side to the three statuses this page manages (BUILD_PLAN
 * §4a's D3 scope) — Pending stays the Approval Queue's business and Needs
 * Documentation has no defined transition here — PLUS Voided, since P8
 * follow-up F1 (2026-09-10): a voided record is kept forever (DD2, "void,
 * don't delete") and needs somewhere to be SEEN, read-only, behind Manage
 * Records' "Show voided" toggle. Nothing on this page can act on a Voided
 * row; the RPCs refuse it too (void_absence_request/reverse_decision both
 * check status first).
 *
 * And Needs Documentation, since F7 (migration 0026): a record waiting on a
 * doctor's note is a LIVE record with one job left — resolve it. It renders
 * as its own card with the resolution buttons (and void/notes), never the
 * day editors: resolve first, then edit.
 */
export async function loadManageableRecords(): Promise<AbsenceFactRow[]> {
  const { data, error } = await supabase
    .from('absence_facts')
    .select('*')
    .in('status', ['Approved', 'Denied', 'Recorded', 'Voided', 'Needs Documentation'])
    .order('absence_date', { ascending: false });
  if (error) throw new Error(`absence_facts (manage records): ${error.message}`);
  return (data ?? []) as AbsenceFactRow[];
}
